

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection } from 'components/v2/record/DetailSection';

const CouponDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [coupon, setCoupon] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchCouponData();
    }, [id]);

    const fetchCouponData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/coupons/${id}`);
            setCoupon(response.data);
        } catch (error) {
            console.error('Error fetching coupon data:', error);
            setSnackbar({ open: true, message: 'Error loading coupon details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setCoupon(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/coupons/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Coupon updated successfully', severity: 'success' });
            fetchCouponData();
        } catch (error) {
            console.error('Error updating coupon:', error);
            setSnackbar({ open: true, message: 'Error updating coupon', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this coupon?')) {
            try {
                await axiosInstance.delete(`/coupons/${id}`);
                setSnackbar({ open: true, message: 'Coupon deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/coupons'), 1000);
            } catch (error) {
                console.error('Error deleting coupon:', error);
                setSnackbar({ open: true, message: 'Error deleting coupon', severity: 'error' });
            }
        }
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((field) => (
                    <Grid item xs={12} md={6} key={field.field}>
                        <InlineEditField
                            label={field.label}
                            value={coupon?.[field.field]}
                            options={field.options || null}
                            onSave={(newValue) => handleFieldUpdate(field.field, newValue)}
                            isEditing={isEditing}
                            readOnly={field.readOnly || false}
                            renderValue={field.renderValue || ((val) => (val ? String(val) : '-'))}
                        />
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>
                
            </DashboardLayout>
        );
    }

    if (!coupon) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>Coupon not found</MDTypography>
                </MDBox>
                
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        {coupon.name}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Coupon Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchCouponData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Coupon">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete Coupon">
                                        <IconButton
                                            onClick={handleDelete}
                                            sx={commonIconButtonStyles}
                                        >
                                            <Delete sx={commonIconStyles} />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Coupon Information", [
                                    { label: "Coupon Name", field: "name" },
                                    { label: "Code", field: "code" },
                                    { label: "Trip", field: "trip_id", readOnly: true, renderValue: (val) => coupon.trip?.name || val },
                                    { label: "Departure Date", field: "departure_date_id", readOnly: true, renderValue: (val) => coupon.departure_date?.name || val },
                                    { label: "Discount Type", field: "discount_type", options: [{ value: "Fixed", label: "Fixed" }, { value: "Percent", label: "Percent" }] },
                                    { label: "Currency", field: "currency" },
                                    { label: "Value", field: "value", renderValue: (val) => val ? `$${parseFloat(val).toFixed(2)}` : '-' },
                                    { label: "Available Count", field: "available_count" },
                                    { label: "Used Count", field: "used_count" },
                                    { label: "Trip Option", field: "option_id", readOnly: true, renderValue: (val) => coupon.option?.name || val || '-' },
                                    { label: "Customer", field: "customer_id", readOnly: true, renderValue: (val) => coupon.customer?.name || val || '-' },
                                    { label: "Activation Date", field: "activation_date", readOnly: true, renderValue: (val) => val ? formatDate(val, 'date') : '-' },
                                    { label: "Expiration Date", field: "expiration_date", readOnly: true, renderValue: (val) => val ? formatDate(val, 'date') : '-' },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name", readOnly: true },
                                    { label: "Modified By", field: "updated_by_name", readOnly: true },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default CouponDetail;

